import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from './i18n/LanguageContext';

/**
 * Full-screen lockout page for banned accounts.
 * Reads ban info from sessionStorage (set by login error handler) or fetches
 * via /api/auth/account-lockout-info?email=... if the user lands here directly.
 * No header, no sidebar — banned users have no app access.
 */
const AccountBanned = ({ isDarkMode = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = sessionStorage.getItem('lockout_info');
    if (stored) {
      try {
        const info = JSON.parse(stored);
        if (info.code === 'banned') {
          setReason(info.reason || '');
          setLoading(false);
          return;
        }
      } catch {}
    }
    // No stored info — try to fetch using email if available, else send to login
    const email = sessionStorage.getItem('lockout_email') || localStorage.getItem('userEmail') || '';
    if (!email) {
      navigate('/');
      return;
    }
    const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
    fetch(`${API}/auth/account-lockout-info?email=${encodeURIComponent(email)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.code === 'banned') {
          setReason(data.reason || '');
        } else {
          // Either active or unknown — no business being on this page
          navigate('/');
        }
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#111827' : '#f9fafb' }}>
        <div style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}>{t('loading_ellipsis')}</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: isDarkMode ? '#111827' : '#f9fafb',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        maxWidth: 520,
        width: '100%',
        background: isDarkMode ? '#1f2937' : '#ffffff',
        borderRadius: 16,
        padding: '40px 32px',
        textAlign: 'center',
        boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
        border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🚫</div>
        <h1 style={{
          fontSize: 28,
          fontWeight: 700,
          margin: '0 0 16px 0',
          color: isDarkMode ? '#fef2f2' : '#991b1b',
        }}>{t('account_banned_title')}</h1>

        {reason && (
          <div style={{
            background: isDarkMode ? '#7f1d1d' : '#fef2f2',
            border: `1px solid ${isDarkMode ? '#991b1b' : '#fecaca'}`,
            borderRadius: 10,
            padding: '14px 18px',
            margin: '20px 0',
            textAlign: 'left',
          }}>
            <div style={{
              fontSize: 12,
              fontWeight: 600,
              color: isDarkMode ? '#fecaca' : '#991b1b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 4,
            }}>{t('reason_label')}</div>
            <div style={{ fontSize: 15, color: isDarkMode ? '#fef2f2' : '#7f1d1d', lineHeight: 1.5 }}>
              {reason}
            </div>
          </div>
        )}

        <p style={{
          fontSize: 15,
          color: isDarkMode ? '#d1d5db' : '#4b5563',
          lineHeight: 1.6,
          margin: '20px 0',
        }}>
          {t('account_banned_permanent')}
        </p>

        <p style={{
          fontSize: 14,
          color: isDarkMode ? '#9ca3af' : '#6b7280',
          lineHeight: 1.6,
          margin: '20px 0 28px 0',
        }}>
          {t('account_banned_appeal_prefix')}{' '}
          <a href="mailto:support@darijapro.com" style={{ color: '#ef4444', textDecoration: 'underline' }}>
            support@darijapro.com
          </a>{' '}
          {t('account_banned_appeal_suffix')}
        </p>

        <a
          href="mailto:support@darijapro.com"
          style={{
            display: 'inline-block',
            background: '#ef4444',
            color: '#ffffff',
            padding: '12px 32px',
            borderRadius: 8,
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          ✉️ {t('account_email_support')}
        </a>
      </div>
    </div>
  );
};

export default AccountBanned;
